/**
 * Profile Slice for Wadakaroo
 *
 * Manages profile state including:
 * - Profile CRUD operations
 * - Available permissions
 * - Pagination state
 * - Modal states for create/edit
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getProfilesApi,
    getProfileApi,
    createProfileApi,
    updateProfileApi,
    deleteProfileApi,
    getPermissionsGroupedApi,
} from '../../services/profileApi';

// ============================================
// Async Thunks
// ============================================

/**
 * Fetch paginated profiles
 */
export const fetchProfiles = createAsyncThunk(
    'profiles/fetchProfiles',
    async ({ page = 0, size = 10, activeOnly = false, search = '' } = {}, { rejectWithValue }) => {
        try {
            const data = await getProfilesApi({ page, size, activeOnly, search });
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch profiles';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch a single profile by UUID
 */
export const fetchProfile = createAsyncThunk(
    'profiles/fetchProfile',
    async (uuid, { rejectWithValue }) => {
        try {
            const data = await getProfileApi(uuid);
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch profile';
            return rejectWithValue(message);
        }
    }
);

/**
 * Create a new profile
 */
export const createProfile = createAsyncThunk(
    'profiles/createProfile',
    async (profileData, { rejectWithValue }) => {
        try {
            const data = await createProfileApi(profileData);
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to create profile';
            return rejectWithValue(message);
        }
    }
);

/**
 * Update an existing profile
 */
export const updateProfile = createAsyncThunk(
    'profiles/updateProfile',
    async ({ uuid, profileData }, { rejectWithValue }) => {
        try {
            const data = await updateProfileApi(uuid, profileData);
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to update profile';
            return rejectWithValue(message);
        }
    }
);

/**
 * Delete a profile
 */
export const deleteProfile = createAsyncThunk(
    'profiles/deleteProfile',
    async (uuid, { rejectWithValue }) => {
        try {
            await deleteProfileApi(uuid);
            return uuid;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to delete profile';
            return rejectWithValue(message);
        }
    }
);

/**
 * Fetch available permissions grouped by category
 */
export const fetchPermissionsGrouped = createAsyncThunk(
    'profiles/fetchPermissionsGrouped',
    async (_, { rejectWithValue }) => {
        try {
            const data = await getPermissionsGroupedApi();
            return data;
        } catch (error) {
            const message = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to fetch permissions';
            return rejectWithValue(message);
        }
    }
);

// ============================================
// Slice
// ============================================

const initialState = {
    // Profile list
    profiles: [],
    isLoading: false,
    error: null,

    // Pagination
    pagination: {
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0,
        hasNext: false,
        hasPrevious: false,
    },

    // Filters
    filters: {
        activeOnly: false,
        search: '',
    },

    // Selected profile (for edit modal)
    selectedProfile: null,
    isLoadingProfile: false,

    // Available permissions
    permissionGroups: [],
    isLoadingPermissions: false,

    // Modal states
    isCreateModalOpen: false,
    isEditModalOpen: false,
    isDeleteModalOpen: false,
    profileToDelete: null,

    // Operation states
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isRefreshing: false, // Shows spinner overlay on table after create/update/delete
    operationError: null,
    operationSuccess: null,
};

const profileSlice = createSlice({
    name: 'profiles',
    initialState,
    reducers: {
        // Modal controls
        openCreateModal: (state) => {
            state.isCreateModalOpen = true;
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeCreateModal: (state) => {
            state.isCreateModalOpen = false;
            state.operationError = null;
        },
        openEditModal: (state, action) => {
            state.isEditModalOpen = true;
            state.selectedProfile = action.payload;
            state.operationError = null;
            state.operationSuccess = null;
        },
        closeEditModal: (state) => {
            state.isEditModalOpen = false;
            state.selectedProfile = null;
            state.operationError = null;
        },
        openDeleteModal: (state, action) => {
            state.isDeleteModalOpen = true;
            state.profileToDelete = action.payload;
            state.operationError = null;
        },
        closeDeleteModal: (state) => {
            state.isDeleteModalOpen = false;
            state.profileToDelete = null;
            state.operationError = null;
        },

        // Filter controls
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
            state.pagination.page = 0; // Reset to first page when filters change
        },
        setPage: (state, action) => {
            state.pagination.page = action.payload;
        },
        setPageSize: (state, action) => {
            state.pagination.size = action.payload;
            state.pagination.page = 0; // Reset to first page when size changes
        },

        // Clear states
        clearError: (state) => {
            state.error = null;
            state.operationError = null;
        },
        clearOperationSuccess: (state) => {
            state.operationSuccess = null;
        },
        clearSelectedProfile: (state) => {
            state.selectedProfile = null;
        },
    },
    extraReducers: (builder) => {
        // ---- Fetch Profiles ----
        builder.addCase(fetchProfiles.pending, (state) => {
            // Only show full loading state if not refreshing after an operation
            if (!state.isRefreshing) {
                state.isLoading = true;
            }
            state.error = null;
        });
        builder.addCase(fetchProfiles.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isRefreshing = false; // Clear refreshing spinner
            state.profiles = action.payload.content;
            state.pagination = {
                page: action.payload.page,
                size: action.payload.size,
                totalElements: action.payload.totalElements,
                totalPages: action.payload.totalPages,
                hasNext: action.payload.hasNext,
                hasPrevious: action.payload.hasPrevious,
            };
        });
        builder.addCase(fetchProfiles.rejected, (state, action) => {
            state.isLoading = false;
            state.isRefreshing = false; // Clear refreshing spinner
            state.error = action.payload || 'Failed to fetch profiles';
        });

        // ---- Fetch Single Profile ----
        builder.addCase(fetchProfile.pending, (state) => {
            state.isLoadingProfile = true;
        });
        builder.addCase(fetchProfile.fulfilled, (state, action) => {
            state.isLoadingProfile = false;
            state.selectedProfile = action.payload;
        });
        builder.addCase(fetchProfile.rejected, (state, action) => {
            state.isLoadingProfile = false;
            state.operationError = action.payload;
        });

        // ---- Create Profile ----
        builder.addCase(createProfile.pending, (state) => {
            state.isCreating = true;
            state.operationError = null;
        });
        builder.addCase(createProfile.fulfilled, (state, action) => {
            state.isCreating = false;
            state.isCreateModalOpen = false;
            state.isRefreshing = true; // Show spinner while table refreshes
            state.operationSuccess = `Profile "${action.payload.name}" created successfully`;
            // Don't add to list - we'll refetch
        });
        builder.addCase(createProfile.rejected, (state, action) => {
            state.isCreating = false;
            state.operationError = action.payload;
        });

        // ---- Update Profile ----
        builder.addCase(updateProfile.pending, (state) => {
            state.isUpdating = true;
            state.operationError = null;
        });
        builder.addCase(updateProfile.fulfilled, (state, action) => {
            state.isUpdating = false;
            state.isEditModalOpen = false;
            state.selectedProfile = null;
            state.operationSuccess = `Profile "${action.payload.name}" updated successfully`;
            // Update in list
            const index = state.profiles.findIndex(p => p.uuid === action.payload.uuid);
            if (index !== -1) {
                state.profiles[index] = action.payload;
            }
        });
        builder.addCase(updateProfile.rejected, (state, action) => {
            state.isUpdating = false;
            state.operationError = action.payload;
        });

        // ---- Delete Profile ----
        builder.addCase(deleteProfile.pending, (state) => {
            state.isDeleting = true;
            state.operationError = null;
        });
        builder.addCase(deleteProfile.fulfilled, (state, action) => {
            state.isDeleting = false;
            state.isDeleteModalOpen = false;
            state.profileToDelete = null;
            state.operationSuccess = 'Profile deleted successfully';
            // Remove from list
            state.profiles = state.profiles.filter(p => p.uuid !== action.payload);
            state.pagination.totalElements -= 1;
        });
        builder.addCase(deleteProfile.rejected, (state, action) => {
            state.isDeleting = false;
            state.operationError = action.payload;
        });

        // ---- Fetch Permissions ----
        builder.addCase(fetchPermissionsGrouped.pending, (state) => {
            state.isLoadingPermissions = true;
        });
        builder.addCase(fetchPermissionsGrouped.fulfilled, (state, action) => {
            state.isLoadingPermissions = false;
            state.permissionGroups = action.payload;
        });
        builder.addCase(fetchPermissionsGrouped.rejected, (state, action) => {
            state.isLoadingPermissions = false;
            state.operationError = action.payload;
        });
    },
});

// ============================================
// Actions
// ============================================

export const {
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    setFilters,
    setPage,
    setPageSize,
    clearError,
    clearOperationSuccess,
    clearSelectedProfile,
} = profileSlice.actions;

// ============================================
// Selectors
// ============================================

export const selectProfiles = (state) => state.profiles.profiles;
export const selectIsLoading = (state) => state.profiles.isLoading;
export const selectError = (state) => state.profiles.error;
export const selectPagination = (state) => state.profiles.pagination;
export const selectFilters = (state) => state.profiles.filters;
export const selectSelectedProfile = (state) => state.profiles.selectedProfile;
export const selectIsLoadingProfile = (state) => state.profiles.isLoadingProfile;
export const selectPermissionGroups = (state) => state.profiles.permissionGroups;
export const selectIsLoadingPermissions = (state) => state.profiles.isLoadingPermissions;
export const selectIsCreateModalOpen = (state) => state.profiles.isCreateModalOpen;
export const selectIsEditModalOpen = (state) => state.profiles.isEditModalOpen;
export const selectIsDeleteModalOpen = (state) => state.profiles.isDeleteModalOpen;
export const selectProfileToDelete = (state) => state.profiles.profileToDelete;
export const selectIsCreating = (state) => state.profiles.isCreating;
export const selectIsUpdating = (state) => state.profiles.isUpdating;
export const selectIsDeleting = (state) => state.profiles.isDeleting;
export const selectIsRefreshing = (state) => state.profiles.isRefreshing;
export const selectOperationError = (state) => state.profiles.operationError;
export const selectOperationSuccess = (state) => state.profiles.operationSuccess;

export default profileSlice.reducer;